import { createBrowserClient } from '@supabase/ssr';

/**
 * Browser-side Supabase client using @supabase/ssr createBrowserClient.
 * - On Chrome/Firefox: uses cookies with SameSite=None; Secure; Partitioned
 * - On Safari (iframe, 3rd-party cookie blocked): falls back to localStorage
 *   with a best-effort cookie write so the server middleware can still read tokens.
 */

const PFX = 'sb_';

/**
 * Detect whether the browser accepts SameSite=None cookies.
 * Safari in an iframe blocks 3rd-party cookies, so this returns false there.
 */
const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=Lax; Secure`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
    return cache;
  };
})();

function setCookie(name: string, value: string, options?: Record<string, unknown>) {
  if (typeof document === 'undefined') return;
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path ?? '/'}; SameSite=Lax; Secure`;
  if (options?.maxAge != null && options.maxAge !== '') s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires as string).toUTCString()}`;
  document.cookie = s;
}

function fromCookies() {
  if (typeof document === 'undefined') return [];
  return document.cookie
    .split(';')
    .filter(Boolean)
    .map((c) => {
      const [name, ...rest] = c.trim().split('=');
      return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
    })
    .filter((c) => c.name);
}

function fromStorage() {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),

        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;

          if (canUseCookies()) {
            // Chrome / Firefox — cookies only
            cookiesToSet.forEach(({ name, value, options }) => {
              if (value !== undefined && value !== null && value !== '') {
                setCookie(name, value, options as Record<string, unknown>);
              } else {
                document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
              }
            });
          } else {
            // Safari iframe — localStorage primary + best-effort cookie
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                if (value !== undefined && value !== null && value !== '') {
                  localStorage.setItem(`${PFX}${name}`, value);
                  // Best-effort cookie so middleware can still read the token
                  setCookie(name, value, options as Record<string, unknown>);
                } else {
                  localStorage.removeItem(`${PFX}${name}`);
                  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
                }
              } catch {
                // localStorage may be blocked in some private-browsing modes
              }
            });
          }
        },
      },
    }
  );
}
